import React from 'react';
import PropTypes from 'prop-types';

import StyledRadioButton from './styled-radio-button';
import Text from '../../../elements/typography/text/text';

/**
 * [See current version in abstract]
 * (https://app.goabstract.com/projects/86166c10-b73b-11e8-b61c-bbf42f55e4f3/branches/master/collections/9876e8f8-e9cc-453d-89c8-719b79216a11/presentation)
 *
 * RadioButton is used for selecting one option over others.
 * Use the same `name` prop on all the radio buttons representing the different choices.
 *
 * The `checked` prop can be used, but must be used together with `onChange`, or else the radio button will be read-only.
 */
export default function RadioButton({
  id,
  onChange,
  checked,
  disabled,
  labelText,
  labelTextHidden,
  name,
  size,
  children,
  ref
}) {
  return (
    <StyledRadioButton size={size}>
      <input
        ref={ref}
        data-cy="radio-button"
        type="radio"
        id={id}
        onChange={onChange}
        disabled={disabled}
        aria-label={labelText}
        name={name}
        checked={checked}
      />
      <label htmlFor={id}>
        {!labelTextHidden && <Text>{labelText}</Text>}
        {children}
      </label>
    </StyledRadioButton>
  );
}

RadioButton.propTypes = {
  /**
   * Unique identifier for the checkbox
   */
  id: PropTypes.string.isRequired,
  /**
   * Unique name for the collection of radiobuttons
   */
  name: PropTypes.string.isRequired,
  /**
   * Handler to be called when the value of the radiobutton is changed.
   */
  onChange: PropTypes.func,
  /**
   * If true the radiobutton is selected
   */
  checked: PropTypes.bool,
  /**
   * The text label of the radiobutton. This is also used as aria-label for accessibility.
   */
  labelText: PropTypes.string.isRequired,
  /**
   * Boolean indicating if the label text should be hidden
   */
  labelTextHidden: PropTypes.bool,
  /**
   * If true the radiobutton is disabled, and onToggle will not be called on click.
   */
  disabled: PropTypes.bool,
  /**
   * A string indicating if the size should be small (16px) or large (24px).
   */
  size: PropTypes.oneOf(['small', 'large'])
};

RadioButton.defaultProps = {
  disabled: false,
  labelTextHidden: false,
  size: 'large'
};
